import React, { useEffect } from "react";
import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import CustomIcon, { AssetTypes } from "../../../../../../../public/icons-v1_1";
import OrderHistoryOrderInfoContent from "@/features/my-order/features/order-history/components/order-info-section/OrderHistoryOrderInfoContent";
import AppSelect from "@/features/line-sheet-sets/app-select";
import OrderHistoryOrderInfoOrderCancelModal from "@/features/my-order/features/order-history/components/order-info-section/order-info-modals/OrderHistoryOrderInfoOrderCancelModal";
import OrderHistoryOrderInfoOrderModificationModal from "@/features/my-order/features/order-history/components/order-info-section/order-info-modals/OrderHistoryOrderInfoOrderModificationModal";
import OrderHistoryButton from "@/features/my-order/features/order-history/components/OrderHistoryButton";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";
import usePostOrderHistoryUpdateStatus from "@/features/my-order/features/order-history/hooks/usePostOrderHistoryUpdateStatus";
import OrderHistoryOrderInfoOrderSheetModificationModal from "@/features/my-order/features/order-history/components/order-info-section/order-info-modals/OrderHistoryOrderInfoOrderSheetModificationModal";
import usePostOrderHistoryModificationFiles from "@/features/my-order/features/order-history/hooks/usePostOrderHistoryModificationFiles";
import { convertOrderStatusToNew } from "@/shared/utils";
import { getOrderHistoryRenderStatus } from "@/features/my-order/features/order-history/utils";
import useOrderHistoryDetail from "@/features/my-order/features/order-history/hooks/useOrderHistoryDetail";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppDialog from "@/features/ui/hooks/use-app-dialog";
import OrderHistorySectionCard from "@/features/my-order/features/order-history/components/OrderHistorySectionCard";

function OrderHistoryOrderInfoSection() {
  const { tTitle } = useI18nHelper();

  return (
    <OrderHistorySectionCard
      titleKey={"order_history:order_info"}
      iconSrc={"info-circle"}
      actions={<InfoSectionFeatures />}
    >
      <OrderHistoryOrderInfoContent />
    </OrderHistorySectionCard>
  );
}

export default OrderHistoryOrderInfoSection;

type DownloadOptionValue = "original" | "submitted";

type DownloadOptionMeta = {
  name: string;
  value: DownloadOptionValue;
  enabled: boolean;
  unoFileId?: number;
};

const InfoSectionFeatures = () => {
  const {
    isOpen: isCancelOpen,
    onOpen: onCancelOpen,
    onClose: onCancelClose,
  } = useDisclosure();

  const {
    isOpen: isModificationOpen,
    onOpen: onModificationOpen,
    onClose: onModificationClose,
  } = useDisclosure();

  const { fire: fireDownloadUnoFile } = useDownloadUnoFile();
  const { data, orderSheetSetId } = useOrderHistoryDetail();
  const { tTitle, t } = useI18nHelper();
  const { openDialog } = useAppDialog();

  const downloadOptionMetaList: DownloadOptionMeta[] = [
    {
      name: tTitle("order_history:download_original_file"),
      value: "original",
      enabled: !!data?.line_sheet_set?.compressed_imported_file?.id,
      unoFileId: data?.line_sheet_set?.compressed_imported_file?.id,
    },
    {
      name: tTitle("order_history:download_submitted_file"),
      value: "submitted",
      enabled: !!data?.compressed_exported_file?.id,
      unoFileId: data?.compressed_exported_file?.id,
    },
  ];

  // AppSelect에 넘길 옵션 (UI용)
  const refinedOptions = downloadOptionMetaList
    .filter((option) => option.enabled)
    .map(({ name, value }) => ({ name, value }));

  // onSelect에서 빠르게 찾기 위한 맵
  const downloadOptionMap = downloadOptionMetaList.reduce<
    Partial<Record<DownloadOptionValue, DownloadOptionMeta>>
  >((acc, option) => {
    acc[option.value] = option;
    return acc;
  }, {});

  const handleSelectDownload = (value?: string) => {
    // AppSelect 타입이 string으로 올라오는 경우를 안전하게 처리
    if (value !== "original" && value !== "submitted") return;

    const selected = downloadOptionMap[value];
    const unoFileId = selected?.unoFileId;

    if (!unoFileId) return;

    fireDownloadUnoFile({ unoFileId }, {});
  };

  const isSheetDownloadDisabled = downloadOptionMetaList.every(
    (meta) => !meta.enabled
  );

  const { navigateToBuyerOrderSheetSetPage } = useRouterHelper();

  const { postUpdateStatus } = usePostOrderHistoryUpdateStatus(orderSheetSetId);
  const { uploadFiles } = usePostOrderHistoryModificationFiles(orderSheetSetId);

  const { MODIFICATION, MODIFICATION_ONLY, CONFIRMED, ORDER_SHEET } =
    getOrderHistoryRenderStatus(data);

  return (
    <>
      <Flex gap={"16px"}>
        {ORDER_SHEET ? (
          <OrderHistoryButton
            leadingIconSrc={"download"}
            disabled={!data?.uno_form_file?.id}
            onClick={() => {
              if (!data?.uno_form_file?.id) return;
              fireDownloadUnoFile(
                {
                  unoFileId: data.uno_form_file.id,
                },
                {}
              );
            }}
          >
            {tTitle("order_history:sheet_download")}
          </OrderHistoryButton>
        ) : (
          <AppSelect
            name={"시트 다운로드"}
            customTrigger={
              <Flex gap={"4px"} alignItems={"center"}>
                <CustomIcon
                  src={"download"}
                  width={16}
                  height={16}
                  color={"#8F8F8C"}
                />
                <Text>{tTitle("order_history:sheet_download")}</Text>
                <CustomIcon
                  src={"chevron-down"}
                  width={16}
                  height={16}
                  color={"#8F8F8C"}
                />
              </Flex>
            }
            onSelect={(value) => {
              handleSelectDownload(value);
            }}
            options={refinedOptions}
            isDisabled={isSheetDownloadDisabled}
          />
        )}
        {MODIFICATION && (
          <OrderHistoryButton onClick={() => onCancelOpen()}>
            {tTitle("order_history:cancel_order")}
          </OrderHistoryButton>
        )}
        {!ORDER_SHEET && MODIFICATION && (
          <OrderHistoryButton onClick={onModificationOpen}>
            {tTitle("order_history:order_modification")}
          </OrderHistoryButton>
        )}
        {ORDER_SHEET && MODIFICATION && (
          <OrderHistoryButton
            onClick={() => {
              if (!orderSheetSetId || !data?.line_sheet_set.id) return;
              navigateToBuyerOrderSheetSetPage(
                +orderSheetSetId,
                data.line_sheet_set.id,
                undefined,
                undefined,
                undefined,
                !MODIFICATION_ONLY
              );
            }}
          >
            {tTitle("order_history:order_modification")}
          </OrderHistoryButton>
        )}
      </Flex>
      <OrderHistoryOrderInfoOrderCancelModal
        isOpen={isCancelOpen}
        onClose={onCancelClose}
        onConfirm={() => {
          if (!orderSheetSetId) return;

          postUpdateStatus({
            order_sheet_set_ids: [+orderSheetSetId],
            status: "CANCELED",
          });
          onCancelClose();
        }}
      />
      {/*<OrderHistoryOrderInfoOrderSheetModificationModal*/}
      {/*  isOpen={isSheetModificationOpen}*/}
      {/*  onClose={onSheetModificationClose}*/}
      {/*  onConfirm={() => {*/}
      {/*  */}
      {/*  }}*/}
      {/*/>*/}
      <OrderHistoryOrderInfoOrderModificationModal
        isOpen={isModificationOpen}
        onClose={onModificationClose}
        onConfirm={(files) => {
          uploadFiles({ files });

          onModificationClose();
        }}
      />
    </>
  );
};
